import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cx } from "../utils/cx";

interface FieldChromeProps {
  id: string;
  label: string;
  description?: string | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}

function FieldChrome({
  id,
  label,
  description,
  error,
  children,
}: FieldChromeProps) {
  return (
    <label className="ui-field" htmlFor={id}>
      <span className="ui-field__label">{label}</span>
      {children}
      {error ? (
        <span className="ui-field__error" id={`${id}-error`}>
          {error}
        </span>
      ) : description ? (
        <span className="ui-field__description" id={`${id}-description`}>
          {description}
        </span>
      ) : null}
    </label>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  id?: string;
  label: string;
  description?: string | undefined;
  error?: string | undefined;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    { id: providedId, label, description, error, className, ...props },
    ref,
  ) {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const describedBy = error
      ? `${id}-error`
      : description
        ? `${id}-description`
        : undefined;

    return (
      <FieldChrome
        id={id}
        label={label}
        description={description}
        error={error}
      >
        <input
          {...props}
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={cx("ui-input", className)}
        />
      </FieldChrome>
    );
  },
);

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  id?: string;
  label: string;
  description?: string | undefined;
  error?: string | undefined;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    { id: providedId, label, description, error, className, ...props },
    ref,
  ) {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const describedBy = error
      ? `${id}-error`
      : description
        ? `${id}-description`
        : undefined;

    return (
      <FieldChrome
        id={id}
        label={label}
        description={description}
        error={error}
      >
        <textarea
          {...props}
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={cx("ui-input", "ui-textarea", className)}
        />
      </FieldChrome>
    );
  },
);
